import { resolveApiBaseUrl } from "./apiBase";

const API_URL = resolveApiBaseUrl();

const getAuthToken = (): string | null => {
  const token = localStorage.getItem('idToken') || localStorage.getItem('authToken');
  return token ? token.trim() : null;
};

const authHeaders = (contentType = true): HeadersInit => {
  const token = getAuthToken();
  const headers: Record<string, string> = {};
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  if (contentType) {
    headers['Content-Type'] = 'application/json';
  }
  return headers;
};

export type PublicJsonFeedSource = {
  key: string;
  componentSlug: string;
  fields: string[];
  filters: Record<string, string | string[] | boolean>;
};

export const PUBLIC_JSON_FEED_CACHE_MAX_AGE_OPTIONS = [
  { value: 10, label: 'Short (10s)' },
  { value: 300, label: 'Default (300s)' },
  { value: 3600, label: 'Long (3600s)' },
] as const;

export type PublicJsonFeedCacheMaxAge = (typeof PUBLIC_JSON_FEED_CACHE_MAX_AGE_OPTIONS)[number]['value'];

export const DEFAULT_PUBLIC_JSON_FEED_CACHE_MAX_AGE: PublicJsonFeedCacheMaxAge = 300;

export function normalizePublicJsonFeedCacheMaxAge(
  value: unknown
): PublicJsonFeedCacheMaxAge {
  const n = Number(value);
  if (n === 10 || n === 300 || n === 3600) return n;
  return DEFAULT_PUBLIC_JSON_FEED_CACHE_MAX_AGE;
}

export function isPublicJsonFeedShortCache(feed: Pick<PublicJsonFeed, 'cacheMaxAgeSeconds'>): boolean {
  return normalizePublicJsonFeedCacheMaxAge(feed.cacheMaxAgeSeconds) === 10;
}

export type PublicJsonFeed = {
  uid: string;
  title: string;
  status: 'draft' | 'published';
  /** When false, the public export URL returns 404 even if status is published. Defaults to true. */
  enabled?: boolean;
  /** Browser Cache-Control max-age for the public export URL (10, 300, or 3600 seconds). */
  cacheMaxAgeSeconds: PublicJsonFeedCacheMaxAge;
  publicToken: string;
  sources: PublicJsonFeedSource[];
  created?: string;
  updated?: string;
  createdBy?: string | null;
};

export function isPublicJsonFeedEnabled(feed: PublicJsonFeed): boolean {
  return feed.enabled !== false;
}

export function getPublicJsonFeedExportUrl(publicToken: string): string {
  const path = `/api/public/exports/${encodeURIComponent(publicToken)}`;
  if (!API_URL) return path;
  const siteOrigin = API_URL.replace(/\/api\/?$/, '').replace(/\/$/, '');
  return `${siteOrigin}${path}`;
}

export async function fetchJsonFeeds(): Promise<PublicJsonFeed[]> {
  const url = API_URL ? `${API_URL}/public/json-feeds` : '/public/json-feeds';
  const res = await fetch(url, { headers: authHeaders() });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data.message || `Failed to list feeds (${res.status})`);
  }
  if (!data.success) {
    throw new Error(data.message || 'Failed to list feeds');
  }
  return Array.isArray(data.feeds) ? data.feeds : [];
}

export async function fetchJsonFeed(uid: string): Promise<PublicJsonFeed> {
  const url = API_URL ? `${API_URL}/public/json-feeds/${encodeURIComponent(uid)}` : `/public/json-feeds/${encodeURIComponent(uid)}`;
  const res = await fetch(url, { headers: authHeaders() });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data.message || `Failed to load feed (${res.status})`);
  }
  if (!data.success || !data.feed) {
    throw new Error(data.message || 'Failed to load feed');
  }
  return data.feed;
}

export async function createJsonFeed(body: {
  title: string;
  status?: 'draft' | 'published';
  enabled?: boolean;
  cacheMaxAgeSeconds?: PublicJsonFeedCacheMaxAge;
  sources: PublicJsonFeedSource[];
}): Promise<PublicJsonFeed> {
  const url = API_URL ? `${API_URL}/public/json-feeds` : '/public/json-feeds';
  const res = await fetch(url, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(body)
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data.message || `Failed to create feed (${res.status})`);
  }
  if (!data.success || !data.feed) {
    throw new Error(data.message || 'Failed to create feed');
  }
  return data.feed;
}

export async function updateJsonFeed(
  uid: string,
  body: Partial<{
    title: string;
    status: 'draft' | 'published';
    enabled: boolean;
    cacheMaxAgeSeconds: PublicJsonFeedCacheMaxAge;
    sources: PublicJsonFeedSource[];
  }>
): Promise<PublicJsonFeed> {
  const url = API_URL ? `${API_URL}/public/json-feeds/${encodeURIComponent(uid)}` : `/public/json-feeds/${encodeURIComponent(uid)}`;
  const res = await fetch(url, {
    method: 'PUT',
    headers: authHeaders(),
    body: JSON.stringify(body)
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data.message || `Failed to update feed (${res.status})`);
  }
  if (!data.success || !data.feed) {
    throw new Error(data.message || 'Failed to update feed');
  }
  return data.feed;
}

export async function deleteJsonFeed(uid: string): Promise<void> {
  const url = API_URL ? `${API_URL}/public/json-feeds/${encodeURIComponent(uid)}` : `/public/json-feeds/${encodeURIComponent(uid)}`;
  const res = await fetch(url, { method: 'DELETE', headers: authHeaders(false) });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data.message || `Failed to delete feed (${res.status})`);
  }
  if (!data.success) {
    throw new Error(data.message || 'Failed to delete feed');
  }
}

export async function regenerateJsonFeedToken(uid: string): Promise<PublicJsonFeed> {
  const url = API_URL
    ? `${API_URL}/public/json-feeds/${encodeURIComponent(uid)}/regenerate-token`
    : `/public/json-feeds/${encodeURIComponent(uid)}/regenerate-token`;
  const res = await fetch(url, { method: 'POST', headers: authHeaders() });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data.message || `Failed to regenerate token (${res.status})`);
  }
  if (!data.success || !data.feed) {
    throw new Error(data.message || 'Failed to regenerate token');
  }
  return data.feed;
}

export type JsonFeedImportMode = 'create' | 'upsert';

export type JsonFeedImportError = { index: number; message: string };

export async function importJsonFeeds(body: {
  feeds: Partial<PublicJsonFeed>[];
  mode?: JsonFeedImportMode;
}): Promise<{ feeds: PublicJsonFeed[]; errors?: JsonFeedImportError[] }> {
  const url = API_URL ? `${API_URL}/public/json-feeds/import` : '/public/json-feeds/import';
  const res = await fetch(url, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ feeds: body.feeds, mode: body.mode })
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data.message || `Failed to import feeds (${res.status})`);
  }
  if (!data.success) {
    throw new Error(data.message || 'Failed to import feeds');
  }
  return {
    feeds: Array.isArray(data.feeds) ? data.feeds : [],
    ...(Array.isArray(data.errors) && data.errors.length ? { errors: data.errors } : {})
  };
}
